import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SpaceType } from '@/constants/space';
import type {
  TimeSlot,
  TodayAvailabilityResponse,
} from '@/constants/todayAvailability';
import { API_CONFIG } from '@/api';
import { LoungeLogo } from '@/components/elements/LoungeLogo';
import { STATUS_ALT, STATUS_ICONS } from '@/constants/todayAvailability';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface TodayAvailabilityLoungeItemProps {
  airport: string;
  terminal: string;
  lounge: string;
  spaceType: SpaceType;
  flightType: string;
}

export const TodayAvailabilityLoungeItem = ({
  airport,
  terminal,
  lounge,
  spaceType,
  flightType,
}: TodayAvailabilityLoungeItemProps) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<TodayAvailabilityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          space_type: spaceType,
          airport,
          terminal,
          lounge,
        });
        const url = `${BASE_URL}${API_CONFIG.GET.TodayAvailability.path}?${params}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch availability');
        }

        const result: TodayAvailabilityResponse = await response.json();
        setData(result);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('errors.api.todayAvailability.fetchFailed'),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [airport, terminal, lounge, spaceType, t]);

  const formatTimeSlotLabel = (slot: TimeSlot): string => {
    if (i18n.language === 'en') {
      // 英語: AM/PM形式
      const toAmPm = (hour: number, minute: number): string => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const minuteStr = String(minute).padStart(2, '0');
        return `${hour12}:${minuteStr} ${period}`;
      };
      const startTime = toAmPm(slot.start_hour, slot.start_minute);
      const endTime =
        slot.end_hour === null
          ? t('todayAvailability.untilClose')
          : toAmPm(slot.end_hour, 0);
      return `${startTime} - ${endTime}`;
    }

    // 日本語: 24時間形式
    const startTime =
      slot.start_minute > 0
        ? `${slot.start_hour}:${String(slot.start_minute).padStart(2, '0')}`
        : `${slot.start_hour}:00`;
    if (slot.end_hour === null) {
      return `${startTime}~${t('todayAvailability.untilClose')}`;
    }
    return `${startTime}~${slot.end_hour}:00`;
  };

  const getTerminalDisplayName = (
    terminalValue: string,
    flightTypeValue: string,
  ): string => {
    const terminalName = t(`common.terminal.${terminalValue}`);
    const flightTypeName = t(`common.flightType.${flightTypeValue}`);
    return t('common.terminalWithFlightType', {
      terminal: terminalName,
      flightType: flightTypeName,
    });
  };

  if (isLoading) {
    return (
      <div className="dialog__item dialog__item--loading">
        <div className="dialog__place">
          <div className="dialog__airport">
            {t(`common.airport.${airport}`)}
          </div>
          <div className="dialog__terminal">
            {getTerminalDisplayName(terminal, flightType)}
          </div>
        </div>
        <div className="dialog__logo">
          <LoungeLogo
            lounge={lounge}
            className="dialog__logo-img"
            altText={t(`common.lounge.${lounge}`)}
          />
        </div>
        <div className="dialog__schedule">
          <CircularProgress size={24} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="dialog__item dialog__item--error">
        <div className="dialog__place">
          <div className="dialog__airport">
            {t(`common.airport.${airport}`)}
          </div>
          <div className="dialog__terminal">-</div>
        </div>
        <div className="dialog__logo">
          <LoungeLogo
            lounge={lounge}
            className="dialog__logo-img"
            altText={t(`common.lounge.${lounge}`)}
          />
        </div>
        <div className="dialog__schedule">
          <div className="dialog__rows">
            <div className="dialog__time" style={{ color: 'red' }}>
              {t('errors.api.todayAvailability.fetchFailed')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dialog__item">
      <div className="dialog__place">
        <div className="dialog__airport">
          {t(`common.airport.${data.airport}`)}
        </div>
        <div className="dialog__terminal">
          {getTerminalDisplayName(data.terminal, data.flight_type)}
        </div>
      </div>
      <div className="dialog__logo">
        <LoungeLogo
          lounge={data.lounge}
          className="dialog__logo-img"
          altText={t(`common.lounge.${data.lounge}`)}
        />
      </div>
      <div className="dialog__schedule">
        {data.time_slots.map((slot) => (
          <div
            className="dialog__rows"
            key={`${slot.start_hour}-${slot.end_hour}`}
          >
            <div className="dialog__time">{formatTimeSlotLabel(slot)}</div>
            <div className={`dialog__status dialog__status--${slot.status}`}>
              <img
                src={STATUS_ICONS[slot.status]}
                alt={STATUS_ALT[slot.status]}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
